<template>
  <div class="file-uploader">
    <div
      class="drop-zone"
      :class="{ 'drag-over': isDragOver, 'has-error': uploadError }"
      @drop.prevent="handleDrop"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @click="triggerFileInput"
    >
      <div class="drop-zone-content">
        <UploadIcon class="upload-icon" />
        <p class="drop-zone-text">
          {{ s__('Attachments|Drag files here or click to browse') }}
        </p>
        <p class="drop-zone-hint">
          {{ s__('Attachments|Maximum file size: 100 MB') }}
        </p>
      </div>
    </div>

    <input ref="fileInput" type="file" multiple style="display: none" @change="handleFileSelect" />

    <div v-if="selectedFiles.length > 0" class="selected-files">
      <h4>{{ s__('Attachments|Selected Files') }}</h4>
      <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
        <span class="file-name">{{ file.name }}</span>
        <span class="file-size">{{ formatFileSize(file.size) }}</span>
        <button type="button" class="remove-file-btn" @click="removeFile(index)">
          <CloseIcon />
        </button>
      </div>
    </div>

    <div v-if="uploadError" class="error-message">
      <span v-for="(error, index) in uploadError" :key="index">{{ error }}</span>
    </div>

    <div v-if="selectedFiles.length > 0" class="upload-actions">
      <button type="button" class="btn-sm btn-secondary" :disabled="uploading" @click="clearFiles">
        {{ __('Clear') }}
      </button>
      <button
        type="button"
        class="btn-sm btn-primary"
        :disabled="uploading || !canUpload"
        @click="uploadFiles"
      >
        {{ uploading ? s__('Attachments|Uploading...') : s__('Attachments|Upload') }}
      </button>
    </div>

    <div v-if="uploading" class="upload-progress">
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
      </div>
      <span class="progress-text">{{ uploadProgress }}%</span>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { s__, __ } from '@/locale'
import UploadIcon from './icons/UploadIcon.vue'
import CloseIcon from './icons/CloseIcon.vue'

export default {
  name: 'FileUploader',
  components: {
    UploadIcon,
    CloseIcon,
  },
  props: {
    attachableType: {
      type: String,
      required: true,
      validator: value => ['Project', 'Task', 'CalendarEvent'].includes(value),
    },
    attachableId: {
      type: [String, Number],
      required: true,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  emits: ['uploaded', 'error'],
  setup(props, { emit }) {
    const fileInput = ref(null)
    const selectedFiles = ref([])
    const isDragOver = ref(false)
    const uploading = ref(false)
    const uploadProgress = ref(0)
    const uploadError = ref(null)

    const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100 MB

    const totalSize = computed(() => {
      return selectedFiles.value.reduce((sum, file) => sum + file.size, 0)
    })

    const canUpload = computed(() => {
      return selectedFiles.value.length > 0 && !uploading.value
    })

    const triggerFileInput = () => {
      fileInput.value?.click()
    }

    const handleFileSelect = event => {
      const files = Array.from(event.target.files || [])
      addFiles(files)
      // Reset input so the same file can be selected again
      event.target.value = ''
    }

    const handleDrop = event => {
      isDragOver.value = false
      const files = Array.from(event.dataTransfer?.files || [])
      addFiles(files)
    }

    const addFiles = files => {
      uploadError.value = null
      const validFiles = []
      const errors = []

      files.forEach(file => {
        if (file.size > MAX_FILE_SIZE) {
          errors.push(`${file.name}: ${s__('Attachments|File too large (max 100 MB)')}`)
        } else if (file.name.length > 255) {
          errors.push(`${file.name}: ${s__('Attachments|Filename too long (max 255 characters)')}`)
        } else {
          validFiles.push(file)
        }
      })

      if (errors.length > 0) {
        uploadError.value = errors
      }

      selectedFiles.value = [...selectedFiles.value, ...validFiles]
    }

    const removeFile = index => {
      selectedFiles.value.splice(index, 1)
      uploadError.value = null
    }

    const clearFiles = () => {
      selectedFiles.value = []
      uploadError.value = null
    }

    const uploadFiles = async () => {
      if (selectedFiles.value.length === 0) return

      uploading.value = true
      uploadProgress.value = 0
      uploadError.value = null

      try {
        const formData = new FormData()
        selectedFiles.value.forEach(file => formData.append('files[]', file))
        formData.append('attachable_type', props.attachableType)
        formData.append('attachable_id', props.attachableId)

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
        const response = await fetch('/attachments', {
          method: 'POST',
          headers: {
            'X-CSRF-Token': csrfToken,
          },
          body: formData,
          credentials: 'same-origin',
        })

        uploadProgress.value = 100
        const result = await response.json()

        if (!response.ok || result?.errors?.length > 0) {
          uploadError.value = result.errors || [s__('Attachments|Upload failed. Please try again.')]
          emit('error', uploadError.value)
        } else {
          emit('uploaded', result.attachments || [])
          clearFiles()
          uploadProgress.value = 0
        }
      } catch (error) {
        console.error('Upload failed:', error)
        uploadError.value = [s__('Attachments|Upload failed. Please try again.')]
        emit('error', [error.message])
      } finally {
        uploading.value = false
      }
    }

    const formatFileSize = bytes => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    return {
      s__,
      __,
      fileInput,
      selectedFiles,
      isDragOver,
      uploading,
      uploadProgress,
      uploadError,
      canUpload,
      triggerFileInput,
      handleFileSelect,
      handleDrop,
      removeFile,
      clearFiles,
      uploadFiles,
      formatFileSize,
    }
  },
}
</script>

<style scoped>
.file-uploader {
  margin-bottom: 16px;
}

.drop-zone {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
}

.drop-zone:hover {
  border-color: #2aabee;
  background: #f5f5f5;
}

.drop-zone.drag-over {
  border-color: #2aabee;
  background: #e3f2fd;
}

.drop-zone.has-error {
  border-color: #dc2626;
}

.drop-zone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.upload-icon {
  width: 48px;
  height: 48px;
  color: #666;
}

.drop-zone-text {
  font-size: 16px;
  color: #333;
  margin: 0;
}

.drop-zone-hint {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.selected-files {
  margin-top: 16px;
}

.selected-files h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
}

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 4px;
}

.file-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  font-size: 12px;
  color: #666;
}

.remove-file-btn {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.remove-file-btn:hover {
  background-color: #e0e0e0;
  color: #dc2626;
}

.error-message {
  margin-top: 8px;
  padding: 8px 12px;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  font-size: 14px;
}

.error-message span {
  display: block;
  margin-bottom: 4px;
}

.error-message span:last-child {
  margin-bottom: 0;
}

.upload-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 12px;
}

.upload-progress {
  margin-top: 12px;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 4px;
}

.progress-fill {
  height: 100%;
  background: #2aabee;
  transition: width 0.3s;
}

.progress-text {
  font-size: 12px;
  color: #666;
}
</style>
